import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { Button, PageLoader } from "@vionixasia/shared-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faMars,
  faVenus,
  faFileLines,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { useEventId } from "../EventContext";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";
import {
  resolveCertLayout,
  resolveCertSize,
  resolveCertX,
  formatCertValue,
  certFont,
  certFamily,
  loadCertFonts,
} from "../utils/certificateLayout";

// Capitalize the first letter of each word
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Convert nationality to ISO flag code and display name
// Format gender with icon
const formatGender = (gender) => {
  if (!gender || typeof gender !== "string") return "-";
  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === "male" || normalizedGender === "m")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (normalizedGender === "female" || normalizedGender === "f")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return "-";
};

// Get display name for category
const getCategoryDisplay = (category, categories) => {
  const cat = categories.find((c) => c.id === category);
  return cat ? cat.name.trim() : "Unknown Category";
};

// Get start date and time for category
const getStartDateTime = (category, categories, userTimeZone) => {
  const cat = categories.find((c) => c.id === category);
  if (!cat || !cat.startTime) return "Unknown Date";
  return moment(cat.startTime)
    .tz(userTimeZone)
    .format(`DD MMM YYYY [at] HH:mm`);
};

export const ResultsDetail = () => {
  const { eventId: slug, bib } = useParams();
  const { realEventId } = useEventId();
  const eventId = realEventId;
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const db = getFirestore();
  const [categories, setCategories] = useState([]);
  const [eventStatus, setEventStatus] = useState("ongoing");
  const [eventData, setEventData] = useState(null);
  const [runnerData, setRunnerData] = useState(location.state || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  // Fetch categories and event status from Firestore
  useEffect(() => {
    if (!eventId) return;
    
    setLoading(true);
    setError(null);

    const eventRef = doc(db, "event_timing", eventId);
    const unsubscribe = onSnapshot(
      eventRef,
      (doc) => {
        if (doc.exists()) {
          const fetchedEventData = doc.data();
          setEventData(fetchedEventData);
          setCategories(
            fetchedEventData.categories && Array.isArray(fetchedEventData.categories)
              ? fetchedEventData.categories.map((cat) => ({
                  ...cat,
                  // A buzzer-timed category has no start or cut-off: keep them null. new Date(null)
                  // is 1 Jan 1970, which the hero printed as the date and read as
                  // an expired cut-off ("FINISHED" before the race).
                  startTime: !cat.startTime
                    ? null
                    : cat.startTime.toDate
                    ? cat.startTime
                    : new Date(cat.startTime),
                  cutOffTime: !cat.cutOffTime
                    ? null
                    : cat.cutOffTime.toDate
                    ? cat.cutOffTime
                    : new Date(cat.cutOffTime),
                }))
              : []
          );
          setEventStatus(fetchedEventData.status || "ongoing");
        } else {
          setError("Event not found. Please contact the administrator.");
        }
      },
      (error) => {
        setError(`Failed to load event data: ${error.message}`);
      }
    );

    return () => unsubscribe();
  }, [db, eventId]);

  // Fetch runner data from results collection
  useEffect(() => {
    if (!eventId || !bib) return;

    const resultRef = doc(db, "event_timing", eventId, "results", bib);
    const unsubscribe = onSnapshot(
      resultRef,
      (doc) => {
        if (doc.exists()) {
          setRunnerData((prev) => ({
            ...prev,
            ...doc.data(),
            // Preserve calculated fields from location.state if not in doc
            overallRank:
              doc.data().overallRank || prev?.ranking || prev?.overallRank,
            genderRank:
              doc.data().genderRank || prev?.genderRanking || prev?.genderRank,
            finishDuration:
              doc.data().finishDuration || prev?.duration || prev?.finishDuration,
            ageRank:
              doc.data().ageRank || prev?.ageRanking || prev?.ageRank,
          }));
          setLoading(false);
        } else {
          setError("Runner data not found.");
          setLoading(false);
        }
      },
      (error) => {
        setError(`Failed to load runner data: ${error.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, eventId, bib]);

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  if (error || !runnerData) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-red-500 font-oxanium">{error || "Runner not found"}</p>
        <Button
          onClick={() => navigate(location.state?.returnPath || `/${eventId}/results`)}
          color="slate"
          className="mt-4"
        >
          Back to Results
        </Button>
      </div>
    );
  }

  const {
    name,
    overallRank,
    ranking,
    categoryId: category,
    bib: bibNumber,
    nationality,
    gender,
    finishTime: time,
    finishDuration: duration,
    genderRank,
    genderRanking,
    ageRank,
    ageRanking,
    status,
  } = runnerData;

  const generateCertificate = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ---------------------------------------------------------
    // 1. PRE-CALCULATE DATA & LAYOUT
    // ---------------------------------------------------------

    // Derive splits 
    const currentCategory = categories.find((c) => c.id === category);
    let splits = [];

    // Helper to format ISO time string to HH:mm:ss
    const formatTimeStr = (val) => {
        if (!val) return "-";
        try {
            // If it's a Firestore timestamp (object with seconds)
            if (val.seconds || val._seconds) {
                 const ms = (val.seconds || val._seconds) * 1000;
                 return new Date(ms).toLocaleTimeString('en-GB', { 
                    hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" 
                });
            }
            // If string (ISO)
            const date = new Date(val);
            if (isNaN(date.getTime())) return val; // fallback
            return date.toLocaleTimeString('en-GB', { 
                hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" 
            });
        } catch (e) {
            return String(val);
        }
    };

    if (currentCategory?.isLooping) {
        // Generate L1...LN
        const loopCount = Object.keys(runnerData?.loopTimestamps || {}).reduce((mx,k)=>{const n=/^loop(\d+)$/.test(k)?parseInt(k.slice(4),10):0;return n>mx?n:mx;},0);
        const runnerSplitsData = runnerData.checkpoints || {};

        let loopSource = runnerSplitsData;
        if (!loopSource['loop1']) {
             const values = Object.values(runnerSplitsData);
             const found = values.find(v => v.loop1 || v.loop2);
             if (found) loopSource = found;
        }

        for (let i = 1; i <= loopCount; i++) {
             const key = `loop${i}`;
             const loopData = loopSource[key]; 
             let rawTime = loopData?.time || loopData; 
             
             splits.push({
                 checkpointName: `Loop ${i}`,
                 time: formatTimeStr(rawTime)
             });
        }
    } else {
        // Standard Checkpoints
        const definedCheckpoints = currentCategory?.checkpoints || [];
        const runnerSplitsData = runnerData.checkpoints || {}; 

        splits = definedCheckpoints.map((cp) => {
            let timeVal = "-";
            if (typeof runnerSplitsData === 'object') {
                 const entries = Object.values(runnerSplitsData);
                 const match = entries.find(s => 
                    (s.location && s.location?.toLowerCase() === cp.name?.toLowerCase()) ||
                    (s.checkpointName && s.checkpointName?.toLowerCase() === cp.name?.toLowerCase())
                 );
                 if (match) {
                     timeVal = formatTimeStr(match.time || match.timestamp);
                 }
            }
            return {
                checkpointName: cp.name,
                time: timeVal
            };
        });
    }

    // Determine Layout
    //
    // This page used to hardcode a 1200x1600 white certificate, which is why an
    // event configured in the admin designer still downloaded the stock design
    // here: the per-event layout was simply never read. It now shares the same
    // contract as the admin preview and the runner-details modal, so one saved
    // layout drives every place a certificate can be produced.
    const maxRowsPerCol = 4;
    const size = resolveCertSize(eventData?.certificateLayout);
    const L = resolveCertLayout(eventData?.certificateLayout, size);
    canvas.width = size.w;
    canvas.height = size.h;

    const ctx = canvas.getContext("2d");

    // Uploaded typefaces must be registered before the first fillText, or the
    // whole sheet is drawn in the fallback font.
    await loadCertFonts(L.customFonts);

    // ---------------------------------------------------------
    // 2. DRAWING
    // ---------------------------------------------------------

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Per-event artwork, drawn first so text lands on top. A failed load is not
    // fatal - a broken URL should still yield a usable certificate.
    const bgUrl = eventData?.certificateBackground;
    if (bgUrl) {
      try {
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.crossOrigin = "anonymous";
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = bgUrl;
        });
        // Cover, not stretch, so artwork whose ratio differs from the canvas
        // loses edges rather than being squashed.
        const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        ctx.drawImage(img, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
      } catch {
        // keep the plain white base
      }
    }

    if (L.showHeaderBand && L.headerHeight > 0 && !bgUrl) {
      ctx.fillStyle = "#F3F4F6";
      ctx.fillRect(0, 0, canvas.width, L.headerHeight);
      ctx.strokeStyle = "#E5E7EB";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, L.headerHeight);
      ctx.lineTo(canvas.width, L.headerHeight);
      ctx.stroke();
    }

    /** Draw one configured text block, honouring alignment, font and affixes. */
    const drawBlock = (key, raw, total) => {
      const b = L[key];
      if (!b || b.show === false) return;
      const { x, align } = resolveCertX(b, canvas.width);
      ctx.textAlign = align;
      ctx.fillStyle = b.color;
      ctx.font = certFont(b, b.size);
      ctx.fillText(formatCertValue(raw, b, total), x, b.y);
      if (b.showLabel && b.label) {
        ctx.fillStyle = b.labelColor;
        ctx.font = `${b.labelSize}px ${certFamily(b.font)}`;
        ctx.fillText(b.label, x, b.y + (b.labelGap ?? 40));
      }
    };

    const catText = getCategoryDisplay(category, categories);
    const overallRankDisplay = (ranking && ranking > 0) ? ranking : (overallRank && overallRank > 0) ? overallRank : "-";
    const genderRankDisplay = (genderRanking && genderRanking > 0) ? genderRanking : (genderRank && genderRank > 0) ? genderRank : "-";
    const ageRankDisplay = (ageRanking && ageRanking > 0) ? ageRanking : (ageRank && ageRank > 0) ? ageRank : null;

    // Raw values in - each block's own textCase decides capitalisation.
    drawBlock("eventName", eventData?.eventName || "VIONIX EVENT");
    if (L.subtitle.text) drawBlock("subtitle", L.subtitle.text);
    drawBlock("runnerName", name || "");
    drawBlock("categoryBib", `${catText}  |  BIB: ${bibNumber || "-"}`);
    if (L.finishLabel.text) drawBlock("finishLabel", L.finishLabel.text);
    drawBlock("finishTime", duration || "-");

    if (L.divider.show) {
      ctx.strokeStyle = L.divider.color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(100, L.divider.y);
      ctx.lineTo(canvas.width - 100, L.divider.y);
      ctx.stroke();
    }

    // Ranking. The legacy symmetric pair still renders for events laid out
    // before ranks split into independently placed blocks; it is also the only
    // mode that carries the three-column age-group variant this page adds.
    if (L.ranks.show) {
      const rankY = L.ranks.y;
      const gap = ageRankDisplay ? 350 : L.ranks.offset;
      const cols = ageRankDisplay
        ? [
            [overallRankDisplay, "OVERALL RANK", -gap],
            [genderRankDisplay, "GENDER RANK", 0],
            [ageRankDisplay, "AGE GROUP RANK", gap],
          ]
        : [
            [overallRankDisplay, "OVERALL RANK", -gap],
            [genderRankDisplay, "GENDER RANK", gap],
          ];
      ctx.textAlign = "center";
      cols.forEach(([value, label, dx]) => {
        ctx.fillStyle = L.ranks.color;
        ctx.font = `bold ${L.ranks.size}px Arial`;
        ctx.fillText(value, canvas.width / 2 + dx, rankY);
        ctx.fillStyle = L.ranks.labelColor;
        ctx.font = "24px Arial";
        ctx.fillText(label, canvas.width / 2 + dx, rankY + 40);
      });
    } else {
      // "1/100" needs the field size, which this page never loads — ask the
      // results API only when a rank block actually prints it. On failure the
      // bare rank still prints.
      let totals = null;
      if ((L.overallRank?.showTotal || L.genderRank?.showTotal) && category) {
        try {
          const params = new URLSearchParams({ eventId, category, limit: "1" });
          const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/results?${params}`);
          if (res.ok) totals = await res.json();
        } catch {
          // keep the bare rank
        }
      }
      const isMale = (gender || "").toLowerCase() === "male";
      drawBlock("overallRank", overallRankDisplay, totals?.rankedTotal);
      drawBlock("genderRank", genderRankDisplay, isMale ? totals?.rankedTotalMale : totals?.rankedTotalFemale);
    }

    if (L.splits.show) {
      let currentY = L.splits.y;
      const tableWidth = L.splits.width;
      const tableX = (canvas.width - tableWidth) / 2;
      const splitFont = certFamily(L.splits.font);

      ctx.textAlign = "left";
      ctx.fillStyle = L.splits.labelColor;
      ctx.font = `bold 24px ${splitFont}`;
      ctx.fillText("SPLITS", tableX, currentY);

      currentY += 20;
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = L.splits.labelColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(tableX, currentY);
      ctx.lineTo(tableX + tableWidth, currentY);
      ctx.stroke();
      ctx.restore();

      currentY += 60;

      if (splits.length > 0) {
        const numCols = Math.ceil(splits.length / maxRowsPerCol);
        const colGap = 40;
        const colWidth = (tableWidth - ((numCols - 1) * colGap)) / numCols;
        let startX = tableX;

        for (let c = 0; c < numCols; c++) {
          const colSplits = splits.slice(c * maxRowsPerCol, (c + 1) * maxRowsPerCol);
          let rowY = currentY;
          colSplits.forEach((cp) => {
            drawSplitRow(ctx, cp, startX, colWidth, rowY, L);
            rowY += 50;
          });
          startX += colWidth + colGap;
        }
      } else {
        ctx.textAlign = "center";
        ctx.font = `italic 24px ${splitFont}`;
        ctx.fillStyle = L.splits.labelColor;
        ctx.fillText("No checkpoint data recorded", canvas.width / 2, currentY + 40);
      }
    }

    if (L.footer.show) {
      ctx.fillStyle = L.footer.bgColor;
      ctx.fillRect(0, canvas.height - 80, canvas.width, 80);
      ctx.textAlign = "center";
      ctx.fillStyle = L.footer.color;
      ctx.font = "bold 24px Arial";
      ctx.fillText(L.footer.text, canvas.width / 2, canvas.height - 30);
    }

    // Download
    const link = document.createElement("a");
    link.download = `${name}-Result-Recap-${bibNumber}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  // Helper function for drawing a split row
  // Takes the resolved layout so split rows follow the event's configured
  // colours and font instead of the fixed greys that vanish on dark artwork.
  const drawSplitRow = (ctx, cp, x, width, y, L) => {
         const font = certFamily(L?.splits?.font);
         const nameColor = L?.splits?.labelColor || "#374151";
         const timeColor = L?.splits?.color || "#111827";

         // Name
         ctx.textAlign = "left";
         ctx.font = `${L?.splits?.size || 24}px ${font}`;
         ctx.fillStyle = nameColor;
         ctx.fillText(cp.checkpointName, x + 10, y);

         // Time
         ctx.textAlign = "right";
         ctx.font = `bold ${L?.splits?.size || 24}px ${font}`;
         ctx.fillStyle = timeColor;
         ctx.fillText(cp.time || "-", x + width - 10, y);

         // Row hairline, faint so it reads as a rule rather than a table border.
         ctx.save();
         ctx.globalAlpha = 0.25;
         ctx.strokeStyle = nameColor;
         ctx.lineWidth = 1;
         ctx.beginPath();
         ctx.moveTo(x, y + 15);
         ctx.lineTo(x + width, y + 15);
         ctx.stroke();
         ctx.restore();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-red-500 font-oxanium">{error}</p>
      </div>
    );
  }

  if (loading) {
    return <PageLoader />;
  }

  const { code: flagCode, display: nationalityDisplay } =
    getFlagCode(nationality);

  return (
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
        <button
          onClick={() => navigate(location.state?.returnPath || `/${slug}`)}
          className="mb-6 flex items-center text-gray-900 hover:text-gray-700 transition-colors font-oxanium text-sm"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Race Results
        </button>
       
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-[30%] bg-white border rounded-card border-slate-200 shadow-sm flex flex-col items-center text-center">
            <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wide py-4 w-full">
              {capitalizeWords(name)}
            </h3>
            <div className="bg-gray-100 text-gray-900 text-4xl font-extrabold py-3 w-full border-y border-gray-200">
              {bibNumber || "-"}
            </div>
            <div className="py-3 border-b border-gray-200 w-full">
              <p className="text-base font-semibold text-gray-600">
                {getCategoryDisplay(category, categories)}
              </p>
              {/*   <p className="text-base font-semibold text-gray-600">
                Start: {getStartDateTime(category, categories, userTimeZone)}
              </p> */}
              <p className="text-base font-semibold text-gray-600">
                Finish: {duration || "-"}
              </p>
            </div>
            <div className="text-sm text-gray-600 py-3 w-full">
              <p>
                <span className="font-semibold">Overall Rank:</span>{" "}
                {(ranking && ranking > 0) ? ranking : (overallRank && overallRank > 0) ? overallRank : "-"}
                {" | "}
                <span className="font-semibold">Gender Rank:</span>{" "}
                {(genderRanking && genderRanking > 0) ? genderRanking : (genderRank && genderRank > 0) ? genderRank : "-"}
                {((ageRanking && ageRanking > 0) || (ageRank && ageRank > 0)) && (
                   <> | <span className="font-semibold">Age Group Rank:</span>{" "}
                   {(ageRanking && ageRanking > 0) ? ageRanking : ageRank}</>
                )}
              </p>
            </div>
          </div>
          <div className="w-full md:w-[70%] bg-white rounded-card border border-slate-200 overflow-hidden flex flex-col">
            <div className="flex flex-col md:table min-w-full divide-y divide-gray-200 mb-6">
              <div className="hidden md:table-header-group bg-gray-50">
                <div className="table-row">
                  <div className="table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                    Name
                  </div>
                  <div className="table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </div>
                  <div className="table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nationality
                  </div>
                  <div className="table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gender
                  </div>
                  <div className="table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:table-row-group">
                <div className="flex flex-col md:table-row py-2 px-4 md:p-0 border-b md:border-none border-gray-200 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between py-2 md:table-cell md:text-left md:align-middle md:px-4 md:py-4 md:max-w-[150px]">
                    <span className="font-semibold md:hidden text-gray-600">
                      Name:
                    </span>
                    <span className="text-sm text-gray-600 uppercase truncate">
                      {capitalizeWords(name) || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:table-cell md:align-middle md:px-4 md:py-4">
                    <span className="font-semibold md:hidden text-gray-600">
                      Category:
                    </span>
                    <span className="text-sm text-gray-600">
                      {getCategoryDisplay(category, categories)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:table-cell md:align-middle md:px-4 md:py-4">
                    <span className="font-semibold md:hidden text-gray-600">
                      Nationality:
                    </span>
                    <span className="text-sm text-gray-600 inline-flex items-center gap-1">
                      <img
                        src={`https://flagcdn.com/w40/${flagCode}.png`}
                        onError={(e) => {
                          e.target.src = "https://flagcdn.com/w40/un.png";
                        }}
                        className="w-5 h-4 object-cover border border-gray-300 rounded"
                        alt={nationalityDisplay}
                      />
                      {nationalityDisplay || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:table-cell md:align-middle md:px-4 md:py-4">
                    <span className="font-semibold md:hidden text-gray-600">
                      Gender:
                    </span>
                    <span className="text-sm text-gray-600 border border-gray-300 rounded-full px-2 py-1 inline-flex items-center">
                      {formatGender(gender)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:table-cell md:align-middle md:px-4 md:py-4">
                    <span className="font-semibold md:hidden text-gray-600">
                      Status:
                    </span>
                    <span className="text-xs text-gray-800 bg-gray-100 border border-gray-400 rounded-full px-2 py-1">
                      {status || "ON RACE"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center px-4 pb-4">
              <Button
                onClick={generateCertificate}
                color="teal"
                fullWidth={true}
                disabled={!eventData?.enableCertificate}
              >
                <FontAwesomeIcon
                  icon={faFileLines}
                  className={`${
                    eventData?.enableCertificate ? "text-white" : "text-gray-400"
                  } w-4 h-4 mr-2`}
                />
                {eventData?.enableCertificate
                  ? "Download Certificate"
                  : "Certificate Not Available"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </section>
  );
};

export default ResultsDetail;
